import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useNavigate } from "react-router-dom";
import { Colors } from "../utils/colors";
import { API } from "../constant/apiConstant";
import { setAdmin } from "../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { consumeAdminSessionMessage } from "../utils/adminSessionTimeout";

export default function AdminLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "info",
    message: ""
  });

  const showAlert = (type, message, time = 5000) => {
    setAlert({
      open: true,
      type,
      message
    });

    setTimeout(() => {
      setAlert((prev) => ({ ...prev, open: false }));
    }, time);
  };

  useEffect(() => {
    const message = consumeAdminSessionMessage();
    if (message) {
      showAlert("warning", message, 7000);
    }
  }, []);

  const requestLoginOTP = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API.BASE_URL}/api/admin/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await res.json();

      if (!data.status) {
        showAlert("error", data.message || "Invalid admin credentials");
        setLoading(false);
        return;
      }

      showAlert("success", "OTP sent to admin email");
      setStep(2);
    } catch (error) {
      showAlert("error", "Unable to connect to server");
    }

    setLoading(false);
  };

  const verifyOTP = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API.BASE_URL}/api/admin/auth/verify-otp`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          otp
        })
      });

      const data = await res.json();

      if (!data.status) {
        showAlert("error", data.message || "Invalid OTP");
        setLoading(false);
        return;
      }

      dispatch(setAdmin(data.role))
      showAlert("success", "Admin login successful");

      setTimeout(() => {
        navigate("/admin/users");
      }, 1500);
    } catch (error) {
      showAlert("error", "Unable to verify OTP");
    }

    setLoading(false);
  };

  const handleCredentialsSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password || loading) {
      return;
    }

    await requestLoginOTP();
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();

    if (otp.length < 4 || loading) {
      return;
    }

    await verifyOTP();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: Colors.secondary,
        p: 2
      }}
    >
      <Card
        sx={{
          p: 4,
          width: { xs: "100%", md: "40%" },
          maxWidth: { xs: 420, md: "none" },
          minWidth: { md: 520 },
          borderRadius: 2
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Admin Login
        </Typography>

        {alert.open && (
          <Alert severity={alert.type} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        {step === 1 && (
          <Stack component="form" onSubmit={handleCredentialsSubmit} spacing={2}>
            <TextField
              label="Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Typography
              sx={{ textAlign: "right", fontSize: 13, color: Colors.accent, cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password
            </Typography>

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={!email || !password || loading}
              sx={{
                backgroundColor: Colors.secondary,
                color: "white"
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Continue"}
            </Button>

            <Button variant="text" onClick={() => navigate("/presenter/login")}>
              Presenter Login
            </Button>
          </Stack>
        )}

        {step === 2 && (
          <Stack component="form" onSubmit={handleOtpSubmit} spacing={2}>
            <TextField
              label="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
            />

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={otp.length < 4 || loading}
              sx={{
                backgroundColor: Colors.secondary,
                color: "white"
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Verify OTP"}
            </Button>

            <Button
              variant="text"
              onClick={requestLoginOTP}
              disabled={loading}
            >
              Resend OTP
            </Button>

            <Button variant="text" onClick={() => setStep(1)}>
              Back
            </Button>
          </Stack>
        )}
      </Card>
    </Box>
  );
}
